import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { Photo, Video } from "../api/types";
import { VisibilityBadge } from "../components/VisibilityBadge";
import { useAsync } from "../hooks/useAsync";
import { accession } from "../lib/format";
import type { AdminOutletContext } from "./AdminLayout";
import { Thumb, type MediaRow } from "./mediaAdmin";

/** How many of each the home page shows at once (see HomePage's rotating windows). */
const PHOTOS_AT_ONCE = 8;
const VIDEOS_AT_ONCE = 4;

/** Moves an item within a list; returns a new list. */
export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * The front-page editor: exactly which photos and videos the home page rotates
 * through, and in what order. Changes are a draft until saved — then the whole
 * ordering is sent at once, and only the items whose place changed are
 * rewritten in the manifests.
 */
export function AdminFrontPage() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data: saved, loading, error } = useAsync(() => adminApi.getFrontPage(), [refreshToken, reload]);
  const { data: allPhotos } = useAsync(() => adminApi.listPhotos(), [refreshToken]);
  const { data: allVideos } = useAsync(() => adminApi.listVideos(), [refreshToken]);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedNote, setSavedNote] = useState(false);

  useEffect(() => {
    if (saved) {
      setPhotos(saved.photos);
      setVideos(saved.videos);
    }
  }, [saved]);

  const ids = (list: { id: number }[]) => list.map((x) => x.id).join(",");
  const dirty = !!saved && (ids(photos) !== ids(saved.photos) || ids(videos) !== ids(saved.videos));

  async function save() {
    setBusy(true);
    setSaveError("");
    try {
      await adminApi.saveFrontPage(photos.map((p) => p.id), videos.map((v) => v.id));
      setReload((n) => n + 1);
      setSavedNote(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  function discard() {
    if (!saved) return;
    setPhotos(saved.photos);
    setVideos(saved.videos);
  }

  if (error) return <p className="notice notice--err">✕ {error}</p>;
  if (loading && !saved) return <p className="admin-empty">Loading…</p>;

  return (
    <div className="fp">
      <p className="admin-intro">
        The <Link to="/">home page</Link> rotates through these in order —{" "}
        {PHOTOS_AT_ONCE} photos and {VIDEOS_AT_ONCE} videos at a time, moving on every two minutes.
        Items that aren't public only appear there for people allowed to see them.
      </p>

      <div className={`fp__savebar${dirty ? " fp__savebar--dirty" : ""}`}>
        <span>
          {dirty ? "Unsaved changes." : savedNote ? "Saved." : `${photos.length} photos · ${videos.length} videos`}
        </span>
        <span className="grow" />
        <button className="btn btn--ghost" onClick={discard} disabled={!dirty || busy}>
          Discard
        </button>
        <button className="btn" onClick={() => void save()} disabled={!dirty || busy}>
          {busy ? "Saving…" : "Save front page"}
        </button>
      </div>
      {saveError && (
        <p className="notice notice--err" role="alert">
          ✕ {saveError}
        </p>
      )}

      <FrontPageList
        title="Photos"
        kind="PHOTO"
        atOnce={PHOTOS_AT_ONCE}
        items={photos}
        candidates={allPhotos?.items ?? []}
        onChange={(next) => {
          setPhotos(next);
          setSavedNote(false);
        }}
      />
      <FrontPageList
        title="Videos"
        kind="VIDEO"
        atOnce={VIDEOS_AT_ONCE}
        items={videos}
        candidates={allVideos?.items ?? []}
        onChange={(next) => {
          setVideos(next);
          setSavedNote(false);
        }}
      />
    </div>
  );
}

function FrontPageList<T extends MediaRow>({
  title,
  kind,
  atOnce,
  items,
  candidates,
  onChange,
}: {
  title: string;
  kind: "PHOTO" | "VIDEO";
  atOnce: number;
  items: T[];
  candidates: T[];
  onChange: (next: T[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");

  const available = useMemo(() => {
    const chosen = new Set(items.map((i) => i.id));
    const q = query.trim().toLowerCase();
    return candidates
      .filter((c) => !chosen.has(c.id))
      .filter((c) => !q || `${c.title} ${c.tags.join(" ")} ${c.collection}`.toLowerCase().includes(q));
  }, [items, candidates, query]);

  return (
    <section className="fp__section">
      <div className="fp__head">
        <h2>
          {title} <span className="cell-muted">({items.length})</span>
        </h2>
        <button className="btn btn--ghost" onClick={() => setAdding((a) => !a)}>
          {adding ? "Close" : `+ Add ${title.toLowerCase()}`}
        </button>
      </div>

      {adding && (
        <div className="fp__picker">
          <input
            type="text"
            value={query}
            autoFocus
            placeholder={`Find ${title.toLowerCase()} by title, tag, or collection…`}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul>
            {available.slice(0, 60).map((c) => (
              <li key={c.id}>
                <Thumb url={c.thumbnailUrl ?? (kind === "PHOTO" ? c.url : null)} />
                <span className="fp__title">{c.title}</span>
                <VisibilityBadge visibility={c.visibility} />
                <button className="admin-link-btn" onClick={() => onChange([...items, c])}>
                  + add
                </button>
              </li>
            ))}
            {available.length === 0 && <li className="cell-muted">Nothing else matches.</li>}
            {available.length > 60 && (
              <li className="cell-muted">…and {available.length - 60} more — narrow the search.</li>
            )}
          </ul>
        </div>
      )}

      {items.length === 0 ? (
        <p className="admin-empty">Nothing here — the home page's {title.toLowerCase()} section will be empty.</p>
      ) : (
        <ol className="fp__list">
          {items.map((item, i) => (
            <li key={item.id} className={i > 0 && i % atOnce === 0 ? "fp__break" : undefined}>
              <span className="fp__pos mono">{i + 1}</span>
              <Thumb url={item.thumbnailUrl ?? (kind === "PHOTO" ? item.url : null)} />
              <span className="fp__title">
                {item.title} <span className="mono cell-muted">{accession(kind, item.id)}</span>
              </span>
              <VisibilityBadge visibility={item.visibility} />
              <span className="fp__moves">
                <button className="admin-link-btn" title="Move to the top" disabled={i === 0}
                        onClick={() => onChange(move(items, i, 0))}>⤒</button>
                <button className="admin-link-btn" title="Move up" disabled={i === 0}
                        onClick={() => onChange(move(items, i, i - 1))}>↑</button>
                <button className="admin-link-btn" title="Move down" disabled={i === items.length - 1}
                        onClick={() => onChange(move(items, i, i + 1))}>↓</button>
                <button className="admin-link-btn admin-link-btn--danger" title="Take off the front page"
                        onClick={() => onChange(items.filter((x) => x.id !== item.id))}>✕</button>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
